/**
 * Which tools the model is given for a given request — §9.3, пункт 1.
 *
 * **The problem this solves.** All 42 tool definitions are 138 KB, or roughly 35k tokens, and they
 * are re-sent on EVERY step of an answer (up to eight) because the proxy does no prompt caching. A
 * typical request uses two to five of them. Measured, not estimated: the button pair alone is 19 KB,
 * the block pair 15 KB, the form/payment pairs 27 KB together.
 *
 * **Three properties make this safe to put in the live chat path**, and they are the whole design:
 *
 * 1. **No match means EVERYTHING.** A request whose words say nothing about what it will touch gets
 *    the full set, exactly as today. So this can only ever remove tools from a turn whose words
 *    pointed elsewhere — it cannot turn a working turn into a refusal by failing to recognise it.
 * 2. **An unknown tool name stays active.** Names are matched against the table, and anything the
 *    table does not mention falls through to always-on. A tool added without touching this file
 *    keeps working; the alternative is a tool that silently disappears from the product.
 * 3. **The model can ask for more.** `request_tools` is always active, and a wrong guess costs one
 *    extra step rather than a "sorry, I cannot do that" — which is a failure this project has
 *    already shipped once, for a different reason (the model refusing to add a form because it did
 *    not believe it had the tool).
 *
 * **Matching is by word prefix, not substring.** `фон` (background) is a substring of `телефон`
 * (telephone), and `сайт` of `сайта`; a naive `includes()` would light up the theme area every time
 * someone mentioned a phone field. Words are split on non-letters and each stem is tested as a
 * PREFIX, which gets Russian inflection right without a stemmer (`блок`, `блока`, `блоки`).
 */
export type ToolArea = 'block' | 'column' | 'image' | 'theme';

/**
 * Always active, whatever the request.
 *
 * Orientation (`get_page_snapshot`), batching (`apply_operations`), the generic
 * `create_widget`/`update_widget`, `get_widget_schema`, and every remove/reorder. The last group is
 * here rather than in an area on purpose: "убери это" and "перенеси выше" carry no noun for the thing
 * being moved, so an intent guess would miss them exactly when the user is most specific about
 * wanting it. They are also the cheap ones.
 *
 * `create_widget`/`update_widget`/`get_widget_schema` joined the core 2026-09-07, replacing what used
 * to be per-widget-type areas (`text`, `button`, `social`, `video`, `indent`, `forms`) — every widget
 * type is withdrawn from `apps/mcp-server`'s `TOOL_FACTORIES` (see
 * [[mcp-generic-widget-schema-lookup]]), so those areas only ever gated tool names that no longer
 * exist. Keeping them would cost a real step for nothing: the model would `request_tools` an area,
 * get back names like `create_text_widget`, and separately discover the name doesn't work — observed
 * live. Moving the three generic widget tools to core removes that detour: they are simply always
 * there, the same way `update_widget` already was before this.
 */
/** The escape hatch's own name — always active, by definition. */
export const REQUEST_TOOLS_NAME = 'request_tools';

export const CORE_TOOL_NAMES: readonly string[] = [
    'get_page_snapshot',
    'apply_operations',
    'create_widget',
    'update_widget',
    'get_widget_schema',
    'remove_widget',
    'remove_block',
    'remove_column',
    'reorder_widget',
    'reorder_block',
    'reorder_column',
    REQUEST_TOOLS_NAME,
];

export const TOOL_AREAS: Readonly<Record<ToolArea, readonly string[]>> = {
    block: ['save_block'],
    // Split from the block on 2026-09-01, measured on a real request: "добавь блок с синим фоном"
    // was carrying both column tools (10 600 of 39 243 tool bytes) for a request that cannot use
    // them — the prompt tells the model to ask for columns on `save_block` rather than adding them
    // afterwards. A column request says "колонка"; a block request almost never does.
    column: ['save_column'],
    // No widget-type tool lives here any more — see the core note above. Only the two tools that
    // move real bytes and are worth gating behind an actual picture-shaped request.
    image: ['search_images', 'upload_image'],
    theme: [
        'update_theme_colors',
        'update_theme_font',
        'update_theme_button',
        'get_theme_presets',
        'apply_theme_preset',
    ],
};

/**
 * Word prefixes that point at an area, in the two languages the editor is used in.
 *
 * A stem may belong to SEVERAL areas, and the ones that do are the interesting cases rather than
 * sloppiness: `цвет` is a block's background or the theme's palette, and which one the user means is
 * not knowable from the word — so both are offered and the model picks.
 */
const AREA_STEMS: Readonly<Record<ToolArea, readonly string[]>> = {
    /**
     * Styling words live HERE and not on the column, even though a column has the same knobs: a
     * request about padding or a background is about a block many times more often, and one that
     * really means a column says so. When it does both open — `колонк` is on the column and `скругл`
     * on the block, and the two sets are a union.
     */
    block: [
        'блок',
        'секц',
        'ряд',
        'фон',
        'отступ',
        'паддинг',
        'границ',
        'рамк',
        'обводк',
        'тень',
        'скругл',
        'ширин',
        'высот',
        'выравн',
        'разделит',
        'дивайдер',
        'полос',
        'цвет',
        'block',
        'section',
        'row',
        'background',
        'padding',
        'border',
        'shadow',
        'radius',
        'width',
        'height',
        'divider',
        'color',
        'colour',
    ],
    column: ['колон', 'столб', 'column'],
    image: [
        'картин',
        'изображ',
        'фото',
        'иллюстр',
        'снимок',
        'логотип',
        'баннер',
        'image',
        'picture',
        'photo',
        'logo',
        'banner',
        'illustration',
    ],
    theme: [
        'тема',
        'темы',
        'теме',
        'палитр',
        'типограф',
        'стиль',
        'брендов',
        'фирменн',
        'пресет',
        'шрифт',
        'кнопк',
        'цвет',
        'theme',
        'palette',
        'typography',
        'preset',
        'brand',
        'font',
        'primary',
        'secondary',
    ],
};

/**
 * Words that mean "this request is about the whole page", which is the one case where guessing is
 * hopeless and wrong: a page built from scratch touches every area, and offering half of them would
 * make the model plan around the tools it happens to see.
 */
const WHOLE_PAGE_STEMS: readonly string[] = [
    'лендинг',
    'страниц',
    'сайт',
    'посадочн',
    'шаблон',
    'landing',
    'page',
    'site',
    'template',
];

/** Words, lowercased, split on anything that is not a letter or digit. */
function words(text: string): string[] {
    return text
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
}

function matches(tokens: readonly string[], stems: readonly string[]): boolean {
    return tokens.some((token) => stems.some((stem) => token.startsWith(stem)));
}

/**
 * The areas a request is about, or `null` for "cannot tell — give everything".
 *
 * `null` rather than an empty array so the caller cannot mistake "nothing matched" for "nothing is
 * needed" and hand the model an empty tool set.
 */
export function selectToolAreas(text: string): ToolArea[] | null {
    const tokens = words(text);

    if (!tokens.length || matches(tokens, WHOLE_PAGE_STEMS)) {
        return null;
    }

    const areas = (Object.keys(TOOL_AREAS) as ToolArea[]).filter((area) => matches(tokens, AREA_STEMS[area]));

    return areas.length ? withImplied(areas) : null;
}

/**
 * Areas that drag another one along.
 *
 * `forms: ['catalog']` lived here until 2026-09-01, when the `list_*` tools were taken off the
 * model's surface (widgets stay visual-only for now — see `registry.ts`). It goes back with them.
 */
const AREA_IMPLIES: Partial<Record<ToolArea, readonly ToolArea[]>> = {
    // "раздели на две колонки" is done with `save_block({ columns: 2 })`, not `save_column` — the
    // prompt says so in as many words. A column request that could not see `save_block` would be
    // pushed towards the call the prompt tells it not to make.
    column: ['block'],
};

function withImplied(areas: readonly ToolArea[]): ToolArea[] {
    return [...new Set(areas.flatMap((area) => [area, ...(AREA_IMPLIES[area] ?? [])]))];
}

const AREA_OF_TOOL = new Map<string, ToolArea>(
    (Object.entries(TOOL_AREAS) as [ToolArea, readonly string[]][]).flatMap(([area, names]) =>
        names.map((name) => [name, area] as const)
    )
);

/**
 * The tools to activate for `text`, out of the names actually available.
 *
 * Filters the CALLER's list rather than returning names from the table: the source of truth for what
 * exists is `tools/list`, and a name this file has never heard of must stay active (property 2 in
 * the module note) — otherwise adding a tool and forgetting this file removes it from the product
 * with nothing failing anywhere.
 */
export function resolveOpenAreas(text: string, extraAreas: readonly ToolArea[] = []): ToolArea[] | null {
    const selected = selectToolAreas(text);

    // `null` already means every area; widening it further is not a narrower answer, and treating
    // the two together would turn "give everything" plus one requested area into JUST that area.
    return selected ? withImplied([...selected, ...extraAreas]) : null;
}

export function activeToolNames(
    available: readonly string[],
    text: string,
    extraAreas: readonly ToolArea[] = []
): string[] {
    const selected = resolveOpenAreas(text, extraAreas);

    if (!selected) {
        return [...available];
    }

    const open = new Set<ToolArea>(selected);

    return available.filter((name) => {
        const area = AREA_OF_TOOL.get(name);

        return area === undefined ? true : open.has(area);
    });
}

/** The area names the model may ask for, for `request_tools`' own description. */
export const TOOL_AREA_NAMES: readonly ToolArea[] = Object.keys(TOOL_AREAS) as ToolArea[];
